import React, { useState, useEffect } from "react";
import { Search, Upload, ImageOff, Play, MoreVertical, Edit, Trash2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import UploadMediaPopup from "../components/Modals/UploadMediaPopup";
import ActionModal from "../components/Modals/ActionModal";
import { useNavigate } from "react-router-dom";
// import adminApi from "../services/adminApi";
import api from "../../../services/api";
import { Pagination } from "../../../services/helper";
const API_ROOT = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace("/api", "");


// Thumbnail Component
function MediaThumbnail({ image, title, onPlay }) {
  const [imgError, setImgError] = useState(false);
  const hasImage = image && !imgError;

  return (
    <div className="relative w-full h-full">
      {!hasImage ? (
        <div className="w-full h-full bg-gray-200 flex flex-col items-center justify-center text-gray-500">
          <ImageOff size={34} strokeWidth={1.8} />
          <span className="text-xs mt-2 font-medium">No Image</span>
        </div>
      ) : (
        <img
          src={image?.startsWith("/uploads") ? `${API_ROOT}${image}` : image}

          alt={title}
          onError={() => setImgError(true)}
          className="w-full h-full object-cover"
        />
      )}

      {hasImage && (
        <button
          onClick={onPlay}
          className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
        >
          <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform">
            <Play size={28} fill="#1F3A60" className="ml-1 text-[#1F3A60]" />
          </div>
        </button>
      )}
    </div>
  );
}

// Video Preview Modal
function VideoPreviewModal({ video, onClose }) {
  if (!video) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-[999] flex items-center justify-center p-4" onClick={onClose}>
      <div className="relative max-w-4xl w-full bg-black rounded-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white z-10">
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <video
          src={video?.media_url?.startsWith("/uploads") ? `${API_ROOT}${video?.media_url}` : video?.media_url}
          controls
          autoPlay
          className="w-full aspect-video"
          poster={video?.thumbnail?.startsWith("/uploads") ? `${API_ROOT}${video?.thumbnail}` : video?.thumbnail}
        >
          Your browser does not support the video tag.
        </video>

        <div className="p-4 bg-black/90 text-white">
          <h3 className="text-lg font-semibold">{video.title}</h3>
          <p className="text-sm text-gray-300 mt-1">{video.author}</p>
        </div>
      </div>
    </div>
  );
}

export default function FaithStream() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [openUploadPopup, setOpenUploadPopup] = useState(false);
  const [page, setPage] = useState(1);

  const [search, setSearch] = useState("");
  const [activeMenu, setActiveMenu] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, position: 'bottom' });
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, item: null });
  const [editingItem, setEditingItem] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);

  // TanStack Query - Fetch Media
  const {
    data: mediaResponse,
    isLoading
  } = useQuery({
    queryKey: ["faith-media-items", page, search],
    queryFn: () => api.getMediaItems(page, 9, search, 'all'),
    select: (res) => {
      if (!res?.success) return { data: [], pagination: null };

      return {
        data: res.data || [],
        pagination: res.pagination || null,   // or res.data?.pagination if it's nested
      };
    },
    keepPreviousData: true,
  });

  // Then use it like this:
  const mediaData = mediaResponse?.data || [];
  const pagination = mediaResponse?.pagination;

  // TanStack Query - Fetch Categories
  const { data: categoriesData } = useQuery({
    queryKey: ["stream-categories"],
    queryFn: () => api.getCategoriesList(),
    select: (res) => (res?.success ? res.data || [] : []),
  });
  console.log({ mediaData });
  useEffect(() => {
    setPage(1);
  }, [search]);

  // Delete Mutation
  const deleteMutation = useMutation({
    mutationFn: (id) => api.deleteMediaItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["faith-media-items"] });
      setDeleteModal({ isOpen: false, item: null });
    },
  });

  const toggleMenu = (id, event) => {
    if (activeMenu === id) {
      setActiveMenu(null);
      return;
    }

    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const dropdownHeight = 90;
    const spaceBelow = window.innerHeight - rect.bottom;

    let top = rect.bottom + window.scrollY + 5;
    let position = 'bottom';

    if (spaceBelow < dropdownHeight) {
      top = rect.top + window.scrollY - dropdownHeight - 5;
      position = 'top';
    }

    setMenuPosition({ top, left: rect.right - 140, position });
    setActiveMenu(id);
  };

  const handlePlay = (item) => {
    setSelectedVideo(item);
    setShowVideoModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setShowEditPopup(true);
    setActiveMenu(null);
  };

  const handleDelete = (item) => {
    setDeleteModal({ isOpen: true, item });
    setActiveMenu(null);
  };

  const handleUploadClose = () => {
    queryClient.invalidateQueries({ queryKey: ["faith-media-items"] });
  };

  const handleUpdateMedia = () => {
    setShowEditPopup(false);
    setEditingItem(null);
    queryClient.invalidateQueries({ queryKey: ["faith-media-items"] });
  };

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeMenu && !event.target.closest('.dropdown-menu') && !event.target.closest('.dropdown-trigger')) {
        setActiveMenu(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [activeMenu]);

  return (
    <div style={{ fontFamily: "'Inter', 'DM Sans', 'Segoe UI', sans-serif" }} className="bg-[#FAFAFA]">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5 mb-8">
        <div>
          <h1 className="text-[22px] sm:text-[28px] md:text-[33px] lg:text-[30px] 2xl:text-[30px] font-bold text-gray-900 mb-1 leading-tight">
            Faith Stream
          </h1>
          <p className="text-gray-500 text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px]">
            Manage media content and uploads
          </p>
        </div>

        <div className="w-full md:w-auto flex flex-col sm:flex-row md:justify-end gap-3">
          <button
            onClick={() => navigate("/admin/categories")}
            className="flex items-center justify-center gap-2 w-full sm:w-auto h-[46px] px-5 rounded-xl border border-[#1F3A60] text-[#1F3A60] bg-white hover:bg-[#1F3A60] hover:text-white transition-colors"
          >
            View Stream Categories
          </button>

          <button
            onClick={() => setOpenUploadPopup(true)}
            className="flex items-center justify-center gap-2 w-full sm:w-auto h-[46px] px-5 rounded-xl bg-[#1F3A60] text-white hover:opacity-90 transition-all"
          >
            <Upload size={15} />
            Upload Media
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="relative w-full sm:w-[292px] h-[41px] mb-6">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-black" />
        <input
          type="text"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-full pl-11 pr-4 text-[14px] rounded-full outline-none bg-white border border-transparent focus:border-blue-400"
        />
      </div>

      {/* Media Grid */}
      {isLoading ? (
        <div className="text-center py-16 text-gray-400">Loading media...</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
          {mediaData?.map((item) => (
            <div key={item.id} className="group bg-white rounded-2xl p-3 transition-all relative">
              <div className="relative rounded-2xl overflow-hidden mb-3 aspect-video bg-gray-100 shadow-sm group-hover:shadow-md transition-shadow">
                <MediaThumbnail
                  image={item.thumbnail}
                  title={item.title}
                  onPlay={() => handlePlay(item)}
                />
              </div>

              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1 leading-snug line-clamp-2">
                    {item.title}
                  </h3>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                    {item.author}
                  </p>
                </div>

                <button
                  onClick={(e) => toggleMenu(item.id, e)}
                  className="dropdown-trigger text-gray-400 hover:text-gray-700 transition-colors flex-shrink-0 mt-1"
                >
                  <MoreVertical size={18} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!isLoading && mediaData?.length === 0 && (
        <div className="text-center py-16 text-gray-400">No media found.</div>
      )}
      <Pagination pagination={pagination} onPageChange={setPage} />

      {/* Dropdown Menu */}
      {activeMenu !== null && (
        <div className="fixed z-50 dropdown-menu" style={{ top: menuPosition.top, left: menuPosition.left }}>
          <div className="w-36 bg-white rounded-xl shadow-xl border py-1">
            {(() => {
              const item = mediaData.find(i => i.id === activeMenu);
              if (!item) return null;
              return (
                <>
                  <button onClick={() => handleEdit(item)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm hover:bg-gray-50">
                    <Edit size={14} /> Edit
                  </button>
                  <button onClick={() => handleDelete(item)} className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                    <Trash2 size={14} /> Delete
                  </button>
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* Upload Popup */}
      {/* <UploadMediaPopup ... /> */}
      {openUploadPopup && (
        <UploadMediaPopup
          onClose={() => { setOpenUploadPopup(false); }}
          categories={categoriesData}
        />
      )}
      {/* Video Preview Modal */}
      {showVideoModal && selectedVideo && (
        <VideoPreviewModal
          video={selectedVideo}
          onClose={() => {
            setShowVideoModal(false);
            setSelectedVideo(null);
          }}
        />
      )}

      {/* Delete Modal */}
      <ActionModal
        isOpen={deleteModal.isOpen}
        onRequestClose={() => setDeleteModal({ isOpen: false, item: null })}
        onConfirm={() => deleteMutation.mutate(deleteModal.item.id)}
        actionType="delete"
        title={deleteModal.item?.title}
        itemName="Media"
        isLoading={deleteMutation.isPending}
        dangerColor="#DC2626"
        customDescription={`This will permanently delete "${deleteModal.item?.title}". This action cannot be undone.`}
      />

      {/* Edit Popup */}
      {showEditPopup && editingItem && (
        <UploadMediaPopup
          onClose={() => {
            setShowEditPopup(false);
            setEditingItem(null);
          }}
          editData={editingItem}
          onUpdate={handleUpdateMedia}
          categories={categoriesData || []}
        />
      )}
    </div>
  );
}